import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import GetAppIcon from "@mui/icons-material/GetApp";
import TableChartIcon from "@mui/icons-material/TableChart";
import PropTypes from "prop-types";

export const ImportReportModal = ({ open, onClose, report, onConfirm, validationResult }) => {
  if (!open || !report) return null;

  const safeReport = {
    valid_mx: report.valid_mx ?? 0,
    valid_us: report.valid_us ?? 0,
    duplicates: report.duplicates ?? 0,
    rejected: report.rejected ?? 0,
    total: report.total ?? null,
    rejected_file_url: report.rejected_file_url ?? null,
  };

  const total = safeReport.total ?? (safeReport.valid_mx + safeReport.valid_us + safeReport.duplicates + safeReport.rejected);

  const metrics = [
    { label: "Válidos México", value: safeReport.valid_mx, icon: CheckCircleIcon, color: "success" },
    { label: "Válidos EE.UU.", value: safeReport.valid_us, icon: CheckCircleIcon, color: "success" },
    { label: "Duplicados eliminados", value: safeReport.duplicates, icon: WarningAmberIcon, color: "warning" },
    { label: "Rechazados", value: safeReport.rejected, icon: ErrorIcon, color: "error" },
  ];

  const hasVariables = validationResult?.hasVariables;
  const previewRows = hasVariables ? (validationResult?.variableRows || []).slice(0, 3) : [];
  const variableIndices = hasVariables
    ? Array.from({ length: validationResult?.expectedColumns - 1 || 0 }, (_, i) => String(i + 1))
    : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth={hasVariables ? "md" : "sm"} fullWidth>
      <DialogTitle>Reporte de Importación</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

          {/* Column Preview (when variables present) */}
          {hasVariables && previewRows.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'info.main' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TableChartIcon color="info" fontSize="small" />
                <Typography variant="subtitle2">Vista previa de columnas</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Teléfono</TableCell>
                      {variableIndices.map((v) => (
                        <TableCell key={v} sx={{ fontWeight: 'bold' }}>{`{{${v}}}`}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{row.phone}</TableCell>
                        {(row.variables || []).map((val, vi) => (
                          <TableCell key={vi}>{val || '(vacío)'}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Mostrando {previewRows.length} de {validationResult?.variableRows?.length || 0} filas.
                Columnas detectadas: {validationResult?.columnCount || 0} | Esperadas: {validationResult?.expectedColumns || 0}
              </Typography>
            </Paper>
          )}

          {metrics.map(({ label, value, icon: Icon, color }) => (
            <Paper
              key={label}
              variant="outlined"
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderColor: `${color}.main`,
              }}
            >
              <Icon color={color} />
              <Typography variant="body1" sx={{ flex: 1 }}>
                {label}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {value.toLocaleString()}
              </Typography>
            </Paper>
          ))}
          {/* Total */}
          <Paper variant="outlined" sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body1" sx={{ flex: 1 }}>
              Total en archivo
            </Typography>
            <Typography variant="body1" fontWeight="bold">
              {total.toLocaleString()}
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button onClick={onClose} color="inherit">
          Cancelar
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          {report.rejected_file_url && (
            <Button
              startIcon={<GetAppIcon />}
              onClick={() => window.open(report.rejected_file_url, "_blank")}
            >
              Descargar Rechazados
            </Button>
          )}
          <Button onClick={onConfirm} variant="contained">
            Continuar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

ImportReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.shape({
    valid_mx: PropTypes.number,
    valid_us: PropTypes.number,
    duplicates: PropTypes.number,
    rejected: PropTypes.number,
    rejected_file_url: PropTypes.string,
  }),
  onConfirm: PropTypes.func.isRequired,
  validationResult: PropTypes.shape({
    hasVariables: PropTypes.bool,
    variableRows: PropTypes.array,
    columnCount: PropTypes.number,
    expectedColumns: PropTypes.number,
  }),
};
